import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Channel } from 'stream-chat';
import { ProductsService, Product, Negotiation, User, Shipping } from '@vestiaire/api-sdk-angular';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';

import { ChatService } from '@app/+chat/services/chat.service';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { UserService } from '@app/core/user';
import { LocaleService } from '@app/core/i18n';
import { selectReservationState } from '@app/+product/store/reducers';
import { ProductReservation } from '@vestiaire/api-sdk-angular/model/productReservation';
import { ReservationService } from '@app/shared/services/product';
import { CheckReservation } from '../../../+product/store/actions/reservation.action';
import * as fromUser from '@app/core/user/store/reducers';
import { TrackingService } from '@app/analytics/services/tracking.service';
import { TagContainer } from '@app/analytics/models/container';

@Component({
  selector: 'vc-chat-discussion',
  templateUrl: './chat-discussion.component.html',
  styleUrls: ['./chat-discussion.component.scss']
})
export class ChatDiscussionComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  channelId: string;

  channel: Channel;
  userId: string;
  isSeller: boolean;
  product$: Observable<Product>;
  shippingInformation$: Observable<Shipping>;
  negotiation$: Observable<Negotiation>;
  receiver$: Observable<User>;
  email = '';

  senderIsBlocked = false;
  receiverIsBlocked = false;
  ngUnsubscribe = new Subject<void>();
  reservation: ProductReservation;
  productId: string;
  senderIsBanned: boolean;
  receiverIsBanned: boolean;

  constructor(
    private chatService: ChatService,
    private productService: ProductsService,
    private mmaoService: MmaoService,
    private usersService: UserService,
    private localeService: LocaleService,
    private reservationService: ReservationService,
    private store: Store<IAppState>,
    private trackingService: TrackingService
  ) {}

  ngOnInit() {
    this.store.pipe(select(selectReservationState), takeUntil(this.ngUnsubscribe)).subscribe(
      state => {
        const reservation = state && state.productId === this.productId ? state.status : null;
        this.reservation = reservation;
      },
      _ => (this.reservation = null)
    );

    this.chatService.connectionState$.subscribe(
      res => {
        if (res) {
          this.joinChannel();
          this.userId = this.chatService.streamChat.user.id;
        }
      },
      err => console.log('[connectionState] err', err)
    );

    this.store.pipe(select(fromUser.getUserEmail), takeUntil(this.ngUnsubscribe)).subscribe(
      email => (this.email = email),
      error => console.error(error)
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const { channelId } = changes;

    if (channelId && channelId.currentValue && !channelId.isFirstChange()) {
      this.channel = null;
      this.senderIsBlocked = false;
      this.receiverIsBlocked = false;
      this.joinChannel();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  joinChannel() {
    this.chatService
      .joinChannel(this.channelId)
      .then(res => {
        this.channel = res;
        const { product, buyerId, sellerId } = this.channel.data;
        this.productId = product.id;
        this.isSeller = sellerId === this.userId;
        const receiverId = this.isSeller ? buyerId : sellerId;

        // Product information
        this.product$ = this.productService.getProduct(this.productId).pipe(map(r => r.data));

        this.product$.subscribe({
          next: result => {
            this.store.dispatch(new CheckReservation({ productId: result.id }));
          }
        });

        // Shipping information
        this.shippingInformation$ = this.productService
          .getProductShippingInformation(this.productId, this.localeService.getCountry())
          .pipe(map(r => r.data));

        // Receiver User information
        this.receiver$ = this.usersService.findUser(receiverId);

        // Negotiation data
        if (this.isSeller) {
          this.negotiation$ = this.mmaoService
            .getNegotiationsByProduct(this.productId)
            .pipe(map(negotiations => negotiations.find(negotiation => negotiation.buyer.id === buyerId)));
        } else {
          this.negotiation$ = this.mmaoService.getActiveBuyerNegotiation(this.productId);
        }

        this.chatService
          .getBlockedMembers(this.channel)
          .then(bannedUsers => {
            this.senderIsBlocked = bannedUsers.includes(this.userId);
            this.receiverIsBlocked = bannedUsers.includes(receiverId);
          })
          .catch(_ => {});

        this.chatService
          .getBannedMembers(this.channel)
          .then(bannedUsers => {
            this.senderIsBanned = bannedUsers.includes(this.userId);
            this.receiverIsBanned = bannedUsers.includes(receiverId);
          })
          .catch(_ => {});
      })
      .catch(err => console.log('[CHAT] join err', err));
  }

  trackingChat(origin: 'report' | 'message_sent') {
    const payload = {
      event: 'bs_chat',
      chat_action_name: origin ? origin : '',
      chat_context: this.isSeller ? 'mmao_seller' : 'mmao_buyer'
    };

    this.trackingService.send({
      type: 'event',
      container: TagContainer.Analytics,
      payload
    });
  }

  get isReserved(): boolean {
    return this.reservation && this.reservationService.isValid(this.reservation);
  }
}
